export interface AdminUser {
  email: string;
  role: string;
}

const LOCAL_TOKEN_KEY = 'fc_admin_jwt';
const API_URL = 'https://api.formacodetech.com';

/**
 * Validates and authenticates admin login via secure server API.
 */
export async function loginAdmin(
  email: string,
  pass: string
): Promise<{ success: boolean; error?: string; user?: AdminUser }> {
  try {
    const res = await fetch(`${API_URL}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        email: email.trim(),
        password: pass,
      }),
    });

    const data = await res.json();

    if (res.ok && data.success) {
      if (data.token) {
        localStorage.setItem(LOCAL_TOKEN_KEY, data.token);
      }

      return {
        success: true,
        user: data.user || {
          email: email.trim(),
          role: 'admin',
        },
      };
    }

    return {
      success: false,
      error:
        data.error ||
        'Authentication failed. Please check your credentials.',
    };
  } catch (err: any) {
    console.error('Server auth login error:', err);

    return {
      success: false,
      error:
        'Network error connecting to security server. Please ensure the server is running.',
    };
  }
}

/**
 * Checks if active admin session exists via secure server verification.
 */
export async function checkAdminSession(): Promise<AdminUser | null> {
  const token = localStorage.getItem(LOCAL_TOKEN_KEY);

  try {
    const res = await fetch(`${API_URL}/api/admin/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
    });

    if (res.ok) {
      const data = await res.json();

      if (data.authenticated && data.user) {
        return data.user;
      }
    }
  } catch (err) {
    console.warn('Check session error:', err);
  }

  if (!token) {
    localStorage.removeItem(LOCAL_TOKEN_KEY);
  }

  return null;
}

/**
 * Signs out admin from server session.
 */
export async function logoutAdmin(): Promise<void> {
  const token = localStorage.getItem(LOCAL_TOKEN_KEY);

  try {
    await fetch(`${API_URL}/api/admin/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
    });
  } catch {
    /* ignore network errors */
  }

  localStorage.removeItem(LOCAL_TOKEN_KEY);
}

/**
 * Changes admin password securely on the server.
 */
export async function changeAdminPassword(
  currentPassword: string,
  newPassword: string,
  confirmPassword: string
): Promise<{
  success: boolean;
  error?: string;
  message?: string;
}> {
  const token = localStorage.getItem(LOCAL_TOKEN_KEY);

  try {
    const res = await fetch(`${API_URL}/api/admin/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify({
        currentPassword,
        newPassword,
        confirmPassword,
      }),
    });

    const data = await res.json();

    if (res.ok && data.success) {
      localStorage.removeItem(LOCAL_TOKEN_KEY);

      return {
        success: true,
        message: data.message,
      };
    }

    return {
      success: false,
      error: data.error || 'Failed to change password.',
    };
  } catch {
    return {
      success: false,
      error: 'Network error processing password change.',
    };
  }
}

/**
 * Requests a secure password reset token email.
 */
export async function requestForgotPassword(
  email: string
): Promise<{ success: boolean; message: string }> {
  try {
    const res = await fetch(`${API_URL}/api/admin/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email.trim(),
      }),
    });

    const data = await res.json();

    return {
      success: true,
      message:
        data.message ||
        'If the email matches a registered admin, a password reset link has been sent.',
    };
  } catch {
    return {
      success: false,
      message: 'Failed to request password reset. Please try again.',
    };
  }
}

/**
 * Resets admin password using single-use reset token.
 */
export async function resetPasswordWithToken(
  token: string,
  newPassword: string,
  confirmPassword: string
): Promise<{
  success: boolean;
  error?: string;
  message?: string;
}> {
  try {
    const res = await fetch(`${API_URL}/api/admin/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        newPassword,
        confirmPassword,
      }),
    });

    const data = await res.json();

    if (res.ok && data.success) {
      return {
        success: true,
        message: data.message,
      };
    }

    return {
      success: false,
      error: data.error || 'Password reset failed.',
    };
  } catch {
    return {
      success: false,
      error: 'Network error resetting password.',
    };
  }
}